import { toast } from 'react-toastify';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { setUser, updateUser, useAppDispatch } from '@/store';
import type {
  AmbassadorPromotionRequest,
  BatchInviteRequest,
  ReferralConfig,
  ReferralData,
  ReferralHistoryParams,
  ReferralUser,
} from '@/types/referral.types';
import { authApi } from '../api/auth.api';
import { referralApi } from '../api/referral.api';

import { MESSAGES } from '@/constants/messages.constants';

export const REFERRAL_QUERY_KEYS = {
  all: ['referral'] as const,
  data: () => [...REFERRAL_QUERY_KEYS.all, 'data'] as const,
  config: () => [...REFERRAL_QUERY_KEYS.all, 'config'] as const,
  history: (params: any) => [...REFERRAL_QUERY_KEYS.all, 'history', params] as const,
};

export interface ReferralDashboardView {
  /** Raw full response */
  raw: ReferralData;
  myReferralCode: string;
  tier: 'Standard' | 'Ambassador';
  stats: ReferralData['stats'];
  slots: ReferralData['slots'];
  recentPayouts: ReferralData['recentPayouts'];

  /** Sorted newest-first */
  referralHistory: ReferralUser[];

  /** True when the user has filled all available slots */
  slotsAtCapacity: boolean;
}
function selectReferralDashboard(data: ReferralData): ReferralDashboardView {
  const sorted = [...(data.referralHistory || [])].sort(
    (a, b) => new Date(b.joinedAt).getTime() - new Date(a.joinedAt).getTime(),
  );

  const totalSlots = typeof data.slots.total === 'number' ? data.slots.total : Infinity;
  return {
    raw: data,
    myReferralCode: data.myReferralCode,
    tier: data.tier,
    stats: data.stats,
    slots: data.slots,
    recentPayouts: data.recentPayouts,
    referralHistory: sorted,
    slotsAtCapacity: data.slots.used >= totalSlots,
  };
}

function selectReferralConfig(data: ReferralConfig): ReferralConfig & {
  feeShareDisplay: string;
  durationDisplay: string;
} {
  return {
    ...data,
    feeShareDisplay: `${data.feeSharePercent}%`,
    durationDisplay: `${data.durationMonths} month${data.durationMonths === 1 ? '' : 's'}`,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Queries
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch the current user's referral dashboard data.
 * `select` transforms the raw response into a pre-computed view model so
 * components never need to derive values themselves.
 */
export function useReferralData(isAuthenticated: boolean) {
  return useQuery({
    queryKey: REFERRAL_QUERY_KEYS.data(),
    queryFn: referralApi.getReferralData,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    select: selectReferralDashboard,
    enabled: isAuthenticated,
  });
}

/**
 * Fetch the admin-level referral configuration.
 * `select` adds human-readable display strings for the UI.
 */
export function useReferralConfig() {
  return useQuery({
    queryKey: REFERRAL_QUERY_KEYS.config(),
    queryFn: referralApi.getConfig,
    staleTime: 10 * 60 * 1000, // Config rarely changes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 min
    select: selectReferralConfig,
  });
}

/**
 * Fetch paginated referral history.
 */
export function useReferralHistory(params: ReferralHistoryParams, enabled: boolean = true) {
  return useQuery({
    queryKey: REFERRAL_QUERY_KEYS.history(params),
    queryFn: () => referralApi.getReferralHistory(params),
    staleTime: 60 * 1000,
    enabled,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Mutation to update the global referral configuration (admin only).
 * Invalidates the config query on success.
 */
export function useUpdateReferralConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: referralApi.updateConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REFERRAL_QUERY_KEYS.config() });
      toast.success(MESSAGES.TOAST.REFERRAL_CONFIG_UPDATED);
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to update configuration');
    },
  });
}

/**
 * Mutation to promote or demote a user to/from Ambassador status (admin only).
 * Invalidates referral data cache to reflect updated ambassador tier.
 */
export function usePromoteAmbassador() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, payload }: { userId: string; payload: AmbassadorPromotionRequest }) =>
      referralApi.promoteToAmbassador(userId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REFERRAL_QUERY_KEYS.all });
      toast.success(MESSAGES.TOAST.USER_STATUS_UPDATED);
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to update user status');
    },
  });
}

/**
 * Mutation for batch ambassador onboarding via email.
 */
export function useBatchInvite() {
  return useMutation({
    mutationFn: (payload: BatchInviteRequest) => referralApi.batchInvite(payload),
    onSuccess: () => {
      toast.success(MESSAGES.TOAST.INVITES_SENT);
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to send invites');
    },
  });
}

/**
 * Mutation to apply a referral code to the current user.
 */
export function useReferralMutation() {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  return useMutation({
    mutationFn: (code: string) => referralApi.applyReferralCode(code),
    onSuccess: async (_, code) => {
      queryClient.invalidateQueries({ queryKey: REFERRAL_QUERY_KEYS.all });

      // Optimistically update local user state
      dispatch(updateUser({ isReferredUser: true, referralCode: code }));

      // Synchronize exact backend user status
      try {
        const status = await authApi.getStatus();
        if (status.authenticated && status.user) {
          dispatch(setUser(status.user));
        }
      } catch (err) {
        console.error('Failed to sync auth status after applying referral code:', err);
      }
    },
  });
}

/**
 * Mutation to update the current user's OWN referral code (self-service).
 * Refreshes referral data and the auth user so the new code shows everywhere.
 */
export function useUpdateOwnReferralCode() {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  return useMutation({
    mutationFn: (code: string) => referralApi.updateOwnReferralCode(code),
    onSuccess: async (newCode) => {
      queryClient.invalidateQueries({ queryKey: REFERRAL_QUERY_KEYS.all });
      dispatch(updateUser({ referralCode: newCode }));
      try {
        const status = await authApi.getStatus();
        if (status.authenticated && status.user) {
          dispatch(setUser(status.user));
        }
      } catch (err) {
        console.error('Failed to sync auth status after updating referral code:', err);
      }
      toast.success('Referral code updated');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to update referral code');
    },
  });
}

/**
 * Hook to validate a referral code (Public API).
 */
export function useValidateReferralCode() {
  return useMutation({
    mutationFn: (code: string) => referralApi.validateReferralCode(code),
  });
}
